import { z } from "zod";

import { entityIdSchema, isoInstantSchema, localeSchema, slugSchema } from "./primitives";

/**
 * Categories and tags (SPEC §42, §43).
 *
 * Both are site-level and both are localized: the *slug* is shared across
 * languages while the *display name* differs, which is why the name lives in a
 * separate `*_localizations` table.
 */

/** A site-level category (SPEC §42). */
export const categorySchema = z
  .object({
    id: entityIdSchema,
    siteId: entityIdSchema,
    slug: slugSchema,
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export type Category = z.infer<typeof categorySchema>;

/** A localized category name (SPEC §42). */
export const categoryLocalizationSchema = z
  .object({
    categoryId: entityIdSchema,
    siteId: entityIdSchema,
    locale: localeSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(500).optional(),
  })
  .strict();

export type CategoryLocalization = z.infer<typeof categoryLocalizationSchema>;

/** A site-level tag (SPEC §43). */
export const tagSchema = z
  .object({
    id: entityIdSchema,
    siteId: entityIdSchema,
    slug: slugSchema,
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export type Tag = z.infer<typeof tagSchema>;

/** A localized tag name (SPEC §43). */
export const tagLocalizationSchema = z
  .object({
    tagId: entityIdSchema,
    siteId: entityIdSchema,
    locale: localeSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(500).optional(),
  })
  .strict();

export type TagLocalization = z.infer<typeof tagLocalizationSchema>;

/** Join row: entry <-> category (SPEC §55 `entry_categories`). */
export const entryCategorySchema = z
  .object({
    entryId: entityIdSchema,
    categoryId: entityIdSchema,
    siteId: entityIdSchema,
  })
  .strict();

export type EntryCategory = z.infer<typeof entryCategorySchema>;

/** Join row: entry <-> tag (SPEC §55 `entry_tags`). */
export const entryTagSchema = z
  .object({
    entryId: entityIdSchema,
    tagId: entityIdSchema,
    siteId: entityIdSchema,
  })
  .strict();

export type EntryTag = z.infer<typeof entryTagSchema>;

/**
 * A category or tag as consumed by the public site: identity plus a resolved
 * display name for one locale, with an explicit fallback marker.
 *
 * Themes must be able to tell "this has no name in this locale" from "this is
 * named X" — silently borrowing another locale's name is the same class of bug
 * SPEC §11 forbids for articles.
 */
export const resolvedTermSchema = z.object({
  id: entityIdSchema,
  slug: slugSchema,
  name: z.string(),
  description: z.string().optional(),
  /** True when `name` came from the canonical locale rather than `locale`. */
  fallback: z.boolean(),
});

export type ResolvedTerm = z.infer<typeof resolvedTermSchema>;

// ---------------------------------------------------------------------------
// Management payloads (TASK: taxonomy management UI)
// ---------------------------------------------------------------------------

/**
 * Create a term. The slug is the stable, language-neutral identity; the name
 * is the display name for one locale (default: the site's default locale).
 * When no name is given the slug is used, which is what deriving terms from
 * frontmatter does.
 */
export const taxonomyCreateSchema = z.object({
  slug: slugSchema,
  name: z.string().min(1).max(200).optional(),
  /** Locale the name applies to; defaults to the site's default locale. */
  locale: localeSchema.optional(),
});

export type TaxonomyCreateInput = z.infer<typeof taxonomyCreateSchema>;

/** Rename a term or re-slug it. At least one field is required. */
export const taxonomyUpdateSchema = z
  .object({
    slug: slugSchema.optional(),
    name: z.string().min(1).max(200).optional(),
    /** Defaults to the site's default locale. */
    locale: localeSchema.optional(),
  })
  .refine((patch) => patch.slug !== undefined || patch.name !== undefined, {
    message: "Provide a slug or a name",
  });

export type TaxonomyUpdateInput = z.infer<typeof taxonomyUpdateSchema>;
